import { useEffect, useRef, useState } from "react";
import AgoraRTC from "agora-rtc-sdk-ng";
import { isSafari, isIOSSafari, getMediaConstraints } from "../utils/safariHelpers";
import notifier from "../components/Notifier";
import ApiService from "./api-service";
import errorReporter from "./error-reporter";

// Cap music at 30% of master volume during livestreams so slider
// 0-100 maps to Agora 0-30 and the host mic stays audible.
const MUSIC_VOLUME_SCALE = 0.3;

// ---------------------------------------------------------------------------
// Streaming music track
//
// Agora's buffer-source track downloads and decodes the whole file before
// the first note, which on a 60 MB WAV is 10-30 s. This plays the file
// through an <audio> element instead (progressive, starts in ~1 s), routes it
// through Web Audio, and hands the resulting MediaStream track to Agora as a
// custom audio track. The object returned exposes the same surface the
// livestream player already uses on buffer tracks, so it is a drop-in.
//
// Needs CORS on the media host: a cross-origin <audio> element without CORS
// headers feeds silence into Web Audio. canStreamCrossOrigin() checks that
// up front so the caller can fall back to the buffer track.
// ---------------------------------------------------------------------------
let sharedMusicAudioCtx = null;
const getMusicAudioContext = () => {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!sharedMusicAudioCtx || sharedMusicAudioCtx.state === "closed") {
    sharedMusicAudioCtx = new Ctx();
  }
  return sharedMusicAudioCtx;
};

const canStreamCrossOrigin = async (url) => {
  if (!/^https?:\/\//i.test(url)) return true; // blob:/data: are same-origin
  try {
    if (new URL(url).origin === window.location.origin) return true;
  } catch (_) {
    return false;
  }
  try {
    const res = await fetch(url, { method: "HEAD", mode: "cors", cache: "force-cache" });
    return res.ok || res.status === 206;
  } catch (_) {
    // HEAD refused or not covered by CORS: try a one-byte ranged GET.
    try {
      const ctl = new AbortController();
      const res = await fetch(url, { method: "GET", mode: "cors", headers: { Range: "bytes=0-0" }, signal: ctl.signal });
      ctl.abort();
      return res.ok || res.status === 206;
    } catch (_e) {
      return false;
    }
  }
};

const createStreamingMusicTrack = async (url, { playLocally = true } = {}) => {
  const ctx = getMusicAudioContext();
  if (!ctx || typeof ctx.createMediaStreamDestination !== "function") {
    throw new Error("Web Audio unavailable");
  }
  if (ctx.state === "suspended") {
    try { await ctx.resume(); } catch (_) { /* resumed on next gesture */ }
  }

  const audio = new Audio();
  audio.crossOrigin = "anonymous";
  audio.preload = "auto";
  audio.src = url;

  const source = ctx.createMediaElementSource(audio);
  const gain = ctx.createGain();
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 256;
  const destination = ctx.createMediaStreamDestination();
  source.connect(gain);
  gain.connect(analyser);
  gain.connect(destination);
  if (playLocally) gain.connect(ctx.destination);

  await new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("audio element did not become playable")), 20000);
    const done = () => { clearTimeout(timer); resolve(); };
    const fail = () => { clearTimeout(timer); reject(new Error(`audio element error ${audio.error?.code ?? ""}`)); };
    audio.addEventListener("canplay", done, { once: true });
    audio.addEventListener("error", fail, { once: true });
    audio.load();
  });

  const mediaStreamTrack = destination.stream.getAudioTracks()[0];
  const agoraTrack = AgoraRTC.createCustomAudioTrack({
    mediaStreamTrack,
    encoderConfig: { sampleRate: 48000, stereo: true, bitrate: 192 },
  });

  let started = false;
  let stopped = false;
  const levelBuf = new Uint8Array(analyser.fftSize);

  const adapter = {
    isStreaming: true,
    agoraTrack,
    audio,
    onEnded: null,
    get currentState() {
      if (!started || stopped || audio.ended) return "stopped";
      return audio.paused ? "paused" : "playing";
    },
    get duration() {
      return Number.isFinite(audio.duration) ? audio.duration : 0;
    },
    getCurrentTime: () => audio.currentTime,
    seekAudioBuffer: (seconds) => { try { audio.currentTime = seconds; } catch (_) { /* not seekable yet */ } },
    startProcessAudioBuffer: () => {
      started = true; stopped = false;
      audio.play().catch((e) => console.warn("[StreamingMusicTrack] play() rejected:", e?.name));
    },
    resumeProcessAudioBuffer: () => {
      started = true; stopped = false;
      audio.play().catch((e) => console.warn("[StreamingMusicTrack] play() rejected:", e?.name));
    },
    pauseProcessAudioBuffer: () => audio.pause(),
    stopProcessAudioBuffer: () => {
      stopped = true;
      audio.pause();
      try { audio.currentTime = 0; } catch (_) { /* ignore */ }
    },
    // Buffer tracks take 0-100(+); map to a gain of 0-1(+).
    setVolume: (v) => { gain.gain.value = Math.max(0, Number(v) || 0) / 100; },
    getVolumeLevel: () => {
      analyser.getByteTimeDomainData(levelBuf);
      let sum = 0;
      for (let i = 0; i < levelBuf.length; i += 1) { const d = (levelBuf[i] - 128) / 128; sum += d * d; }
      return Math.min(1, Math.sqrt(sum / levelBuf.length) * 2);
    },
    setEnabled: (enabled) => { try { agoraTrack.setEnabled(enabled); } catch (_) { /* ignore */ } },
    on: () => () => {},
    close: () => {
      stopped = true;
      try { audio.pause(); } catch (_) { /* ignore */ }
      audio.removeAttribute("src");
      try { audio.load(); } catch (_) { /* ignore */ }
      try { source.disconnect(); gain.disconnect(); analyser.disconnect(); } catch (_) { /* ignore */ }
      try { agoraTrack.close(); } catch (_) { /* ignore */ }
    },
  };
  audio.addEventListener("ended", () => {
    stopped = true;
    if (typeof adapter.onEnded === "function") adapter.onEnded();
  });
  return adapter;
};

const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 30000;
// Disconnect reasons after which rejoining is either unwanted or pointless.
const NO_REJOIN_REASONS = ["LEAVE", "UID_BANNED", "IP_BANNED", "CHANNEL_BANNED"];

const reportAgoraError = ({ message, error, extra }) =>
  errorReporter.captureError({
    message: error ? `${message}: ${error.message || error}` : message,
    stack: error?.stack,
    errorType: "AgoraRTC",
    extra,
  });

/**
 * A custom React hook that:
 *  - Joins/Leaves an Agora channel
 *  - Publishes/Unpublishes local audio/video (if host)
 *  - Provides methods for playing a buffer-source audio track (music)
 *  - Tracks remote publishing users in `remoteUsers`
 */
export function useAgoraRTC({
  appId,
  channel,
  token,
  uid = null,         // optional numeric UID
  role = "host",      // "host" or "audience"
  enableAudio = false,
  enableVideo = false,
  onJoinSuccess,
  isObs = false,      // whether this is an OBS stream
}) {
  // Local track states
  const [localAudioTrack, setLocalAudioTrack] = useState(null);
  const [localVideoTrack, setLocalVideoTrack] = useState(null);
  const [localScreenTrack, setLocalScreenTrack] = useState(null);
  const [isScreenSharing, setIsScreenSharing] = useState(false);

  // Remote users who have published tracks
  const [remoteUsers, setRemoteUsers] = useState([]);

  // The Agora client reference
  const clientRef = useRef(null);

  // Keep track of a buffer-source track for music playback
  const [musicTrack, setMusicTrack] = useState(null);
  // Buffer-source music tracks are owned here, not by callers. `musicTrackRef`
  // mirrors the state for use inside async code (state closures went stale and
  // stopMusicTrack silently did nothing). `liveMusicTracksRef` holds every
  // track created and not yet closed, so leave/stop can never orphan one.
  // `musicLoadSeqRef` lets a newer load, a stop, or a timeout invalidate a
  // load that is still downloading; that load then closes its own track
  // instead of starting to play behind the UI's back.
  const musicTrackRef = useRef(null);
  const liveMusicTracksRef = useRef(new Set());
  const musicLoadSeqRef = useRef(0);

  // Agora connection state, with "RECONNECTING" while this hook waits to rejoin
  const [connectionState, setConnectionState] = useState("DISCONNECTED");

  // Latest props/state for Agora event handlers, which outlive the render that attached them
  const latestRef = useRef({});
  const attachedHandlersRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const leftIntentionallyRef = useRef(false);
  const forceLeaveRef = useRef(null);

  // ==========================================================
  // 1) Initialize the Agora client (only when token is available)
  //    HLS viewers don't get a token, so Agora SDK is not initialized for them
  // ==========================================================
  useEffect(() => {
    if (!clientRef.current && token) {
      clientRef.current = AgoraRTC.createClient({ mode: "live", codec: "h264" });
    }
  }, [token]);

  // ==========================================================
  // 2) Join an Agora channel
  // ==========================================================
  const fetchRenewedToken = async () => {
    const { channel, uid } = latestRef.current;
    const currentUid = clientRef.current?.uid ?? uid;
    const response = await ApiService.LIVE_TOKEN_RENEW({
      channelName: channel,
      ...(Number.isInteger(currentUid) && { uid: currentUid }),
    });
    const renewedToken = response?.result?.token;
    if (!renewedToken) {
      throw new Error(response?.message || "Token renewal returned no token");
    }
    return renewedToken;
  };

  const handleTokenWillExpire = async () => {
    try {
      const renewedToken = await fetchRenewedToken();
      await clientRef.current?.renewToken(renewedToken);
    } catch (error) {
      reportAgoraError({ message: "Token renewal failed", error, extra: { channel: latestRef.current.channel } });
    }
  };

  const rejoinWithFreshToken = async () => {
    const renewedToken = await fetchRenewedToken();
    await joinWithToken(renewedToken);
  };

  // Exponential backoff (1s, 2s, 4s … capped) with unlimited attempts; a
  // successful CONNECTED transition resets the counter.
  const scheduleRejoin = () => {
    if (reconnectTimerRef.current || leftIntentionallyRef.current) return;
    const attempt = reconnectAttemptRef.current;
    const delayMs = Math.min(RECONNECT_BASE_DELAY_MS * 2 ** attempt, RECONNECT_MAX_DELAY_MS);
    setConnectionState("RECONNECTING");
    reconnectTimerRef.current = setTimeout(async () => {
      reconnectTimerRef.current = null;
      reconnectAttemptRef.current = attempt + 1;
      try {
        await rejoinWithFreshToken();
      } catch (error) {
        reportAgoraError({ message: "Rejoin failed", error, extra: { channel: latestRef.current.channel, attempt } });
        scheduleRejoin();
      }
    }, delayMs);
  };

  const handleTokenDidExpire = () => scheduleRejoin();

  const handleConnectionStateChange = (curState, prevState, reason) => {
    console.log(`[Agora] Connection state changed: ${prevState} → ${curState}, reason: ${reason}`);
    if (curState === "CONNECTED") {
      reconnectAttemptRef.current = 0;
    }
    const lostConnection =
      curState === "DISCONNECTED" && !NO_REJOIN_REASONS.includes(reason) && !leftIntentionallyRef.current;
    if (!lostConnection) {
      setConnectionState(curState);
      return;
    }
    reportAgoraError({
      message: `Connection lost (${reason})`,
      extra: { channel: latestRef.current.channel, prevState },
    });
    scheduleRejoin();
  };

  const handleNetworkQuality = (stats) => {
    if (stats.downlinkNetworkQuality <= 2 || stats.uplinkNetworkQuality <= 2) {
      console.warn("[Agora] Poor network quality detected:", stats);
    }
  };

  // Only this hook's handlers are removed, so listeners the page registers
  // on the same client (e.g. Streaming.js "user-left") survive a rejoin.
  const detachClientHandlers = (client) => {
    Object.entries(attachedHandlersRef.current || {}).forEach(([event, handler]) => client.off(event, handler));
    attachedHandlersRef.current = null;
  };

  const attachClientHandlers = (client) => {
    detachClientHandlers(client);
    const handlers = {
      "user-joined": handleUserJoined,
      "user-published": handleUserPublished,
      "user-unpublished": handleUserUnpublished,
      "user-left": handleUserLeft,
      "connection-state-change": handleConnectionStateChange,
      "token-privilege-will-expire": handleTokenWillExpire,
      "token-privilege-did-expire": handleTokenDidExpire,
      "network-quality": handleNetworkQuality,
    };
    Object.entries(handlers).forEach(([event, handler]) => client.on(event, handler));
    attachedHandlersRef.current = handlers;
  };

  const joinChannel = () => joinWithToken(latestRef.current.token);

  const joinWithToken = async (token) => {
    const {
      appId, channel, uid, role, enableAudio, enableVideo, onJoinSuccess,
      localAudioTrack, localVideoTrack, musicTrack, dawAudioTrack,
    } = latestRef.current;
    console.log("[joinChannel] Called with params:", { appId, channel, token, uid, role, enableAudio, enableVideo });

    leftIntentionallyRef.current = false;
    clearTimeout(reconnectTimerRef.current);
    reconnectTimerRef.current = null;

    // Ensure Agora client exists — create inline if the initialization effect
    // hasn't fired yet (race condition between effects in the same render cycle)
    if (!clientRef.current && token) {
      console.log("[joinChannel] Creating Agora client inline (effect hadn't fired yet)");
      clientRef.current = AgoraRTC.createClient({ mode: "live", codec: "h264" });
    }

    const client = clientRef.current;
    if (!client) {
      console.warn("[joinChannel] No Agora client and no token => cannot join");
      return;
    }
    if (!channel) {
      console.warn("[joinChannel] No channel => cannot join");
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      console.error("[joinChannel] getUserMedia not supported => must use HTTPS or modern browser");
      notifier.error("Your browser doesn't support getUserMedia. Please use HTTPS or a modern browser.");
      return;
    }
  
    // Check the connection state before attempting to join
    if (client.connectionState === "CONNECTED") {
      console.log("[joinChannel] Client already connected. Skipping join.");
      return;
    }
    if (client.connectionState === "CONNECTING") {
      console.log("[joinChannel] Client is already connecting. Skipping join.");
      return;
    }
  
    try {
      console.log(`[joinChannel] role=${role}, channel=${channel}, appId=${appId}`);
      await client.setClientRole(role);
  
      // Ensure token is valid (use null if no token is required)
      const validToken = token || null;
      console.log("[joinChannel] Using token:", validToken, " uid:", uid);
  
      // Handlers go on before join so no early event (or reconnect) is missed
      attachClientHandlers(client);

      // Start join + track creation in PARALLEL — camera track resolves in ~100-300ms
      // while join takes ~2.5s. We set localVideoTrack immediately when the camera
      // track is ready so the preview appears BEFORE join completes.
      const joinPromise = client.join(appId, channel, validToken, uid);

      // Existing tracks are reused on a rejoin instead of opening a second mic/camera
      let micTrack = localAudioTrack;
      let camTrack = localVideoTrack;

      if (role === "host") {
        // Create tracks in parallel with join
        const trackPromises = [];

        if (enableAudio && !micTrack) {
          trackPromises.push(
            AgoraRTC.createMicrophoneAudioTrack({
              echoCancellation: false,
              noiseSuppression: true,
              autoGainControl: false,
              AEC: false,
              AGC: false,
              ANS: true,
              encoderConfig: "high_quality_stereo",
            }).then(track => {
              micTrack = track;
              setLocalAudioTrack(track);
            }).catch(err => {
              console.error("[joinChannel] Failed to create mic track:", err);
            })
          );
        }

        if (enableVideo && !camTrack) {
          trackPromises.push(
            AgoraRTC.createCameraVideoTrack({
              encoderConfig: "720p_2",
              optimizationMode: "detail",
              facingMode: "user",
            }).then(track => {
              camTrack = track;
              // Set immediately so Streaming.js can play() it for preview
              // BEFORE join completes (~100-300ms vs ~2.5s)
              setLocalVideoTrack(track);
            }).catch(err => {
              console.error("[joinChannel] Failed to create camera track:", err);
            })
          );
        }

        // Wait for tracks to be created (resolves in ~100-300ms)
        await Promise.all(trackPromises);
      }

      // Wait for join to complete (tracks already created and available for preview)
      await joinPromise;
      const joinedUid = client.uid;
      console.log("[joinChannel] Joined => local UID:", joinedUid);

      if (onJoinSuccess) {
        onJoinSuccess(joinedUid);
      }

      // Publish tracks (created earlier, join now complete)
      if (role === "host") {
        if (micTrack) {
          if (typeof micTrack.setVolume === 'function') {
            micTrack.setVolume(150);
          }
          await client.publish(micTrack);
          console.log("[joinChannel] Mic track published");
        }
        if (camTrack) {
          await client.publish(camTrack);
          console.log("[joinChannel] Camera track published");
        }
        // Lets low-bandwidth viewers fall back to the low-quality stream instead of freezing
        try {
          await client.enableDualStream();
        } catch (error) {
          console.warn("[joinChannel] Dual stream not available:", error?.message);
        }
        // Music/DAW tracks only exist on a rejoin; publish failures must not undo the join
        for (const track of [musicTrack, dawAudioTrack].filter(Boolean)) {
          try {
            await client.publish(track.agoraTrack || track);
          } catch (error) {
            reportAgoraError({ message: "Republish after rejoin failed", error, extra: { channel } });
          }
        }
      }
    } catch (error) {
      console.error("[joinChannel] Error joining channel:", error);
      reportAgoraError({ message: "Join failed", error, extra: { channel, role } });
      throw error;
    }
  };

  // ==========================================================
  // 3) Leave the channel
  // ==========================================================
  const leaveChannel = async (forceful = false) => {
    const client = clientRef.current;
    if (!client) {
      console.warn("[leaveChannel] => no client => cannot leave");
      return;
    }

    leftIntentionallyRef.current = true;
    clearTimeout(reconnectTimerRef.current);
    reconnectTimerRef.current = null;

    try {
      console.log(`[leaveChannel] Unpublishing & closing tracks (forceful=${forceful})...`);

      // Helper function to safely cleanup a track
      const cleanupTrack = async (track, trackName, setter) => {
        if (!track) return;
        try {
          console.log(`[leaveChannel] => unpublish ${trackName}`);
          await client.unpublish(track);
        } catch (err) {
          console.error(`[leaveChannel] Error unpublishing ${trackName}:`, err);
        }
        try {
          track.close();
        } catch (err) {
          console.error(`[leaveChannel] Error closing ${trackName}:`, err);
        }
        setter(null);
      };

      // CRITICAL: Stop all remote audio/video tracks (for audience members)
      if (client.remoteUsers && client.remoteUsers.length > 0) {
        console.log(`[leaveChannel] Stopping ${client.remoteUsers.length} remote users' tracks...`);
        client.remoteUsers.forEach((remoteUser) => {
          try {
            // Stop audio track (this is what plays the livestream audio for audience)
            if (remoteUser.audioTrack) {
              console.log(`[leaveChannel] Stopping remote audio track for uid=${remoteUser.uid}`);
              remoteUser.audioTrack.stop();

              // If forceful (tab close), also try to close the track
              if (forceful) {
                try {
                  remoteUser.audioTrack.close();
                } catch (err) {
                  console.error(`[leaveChannel] Error closing remote audio track:`, err);
                }
              }
            }

            // Stop video track
            if (remoteUser.videoTrack) {
              console.log(`[leaveChannel] Stopping remote video track for uid=${remoteUser.uid}`);
              remoteUser.videoTrack.stop();

              if (forceful) {
                try {
                  remoteUser.videoTrack.close();
                } catch (err) {
                  console.error(`[leaveChannel] Error closing remote video track:`, err);
                }
              }
            }
          } catch (err) {
            console.error(`[leaveChannel] Error stopping remote user ${remoteUser.uid} tracks:`, err);
          }
        });
      }

      // Cleanup all local tracks
      await cleanupTrack(localAudioTrack, "localAudioTrack", setLocalAudioTrack);
      await cleanupTrack(localVideoTrack, "localVideoTrack", setLocalVideoTrack);

      await stopMusicTrack();

      detachClientHandlers(client);

      // Clear state
      setRemoteUsers([]);
      setConnectionState("DISCONNECTED");

      // Leave the channel
      console.log("[leaveChannel] => leaving agora channel...");
      await client.leave();
      console.log("[leaveChannel] => left channel successfully.");

    } catch (error) {
      console.error("[leaveChannel] Fatal error during cleanup:", error);
      // Even if there's an error, ensure state is cleared
      setLocalAudioTrack(null);
      setLocalVideoTrack(null);
      setMusicTrack(null);
      setRemoteUsers([]);
    }
  };

  // ==========================================================
  // 3B) Force leave channel (synchronous cleanup for tab close)
  // ==========================================================
  const forceLeaveChannel = () => {
    const client = clientRef.current;
    if (!client) {
      console.warn("[forceLeaveChannel] => no client => cannot leave");
      return;
    }

    console.log("[forceLeaveChannel] FORCEFUL synchronous cleanup...");
    leftIntentionallyRef.current = true;
    clearTimeout(reconnectTimerRef.current);
    reconnectTimerRef.current = null;

    try {
      // CRITICAL: Immediately stop all remote audio/video tracks
      if (client.remoteUsers && client.remoteUsers.length > 0) {
        console.log(`[forceLeaveChannel] Stopping ${client.remoteUsers.length} remote users' tracks...`);
        client.remoteUsers.forEach((remoteUser) => {
          try {
            if (remoteUser.audioTrack) {
              remoteUser.audioTrack.stop();
              remoteUser.audioTrack.close();
            }
            if (remoteUser.videoTrack) {
              remoteUser.videoTrack.stop();
              remoteUser.videoTrack.close();
            }
          } catch (err) {
            console.error(`[forceLeaveChannel] Error stopping remote user ${remoteUser.uid}:`, err);
          }
        });
      }

      // Stop local tracks
      if (localAudioTrack) {
        try {
          console.log("[forceLeaveChannel] Closing localAudioTrack");
          localAudioTrack.close();
        } catch (err) {
          console.error("[forceLeaveChannel] Error closing local audio:", err);
        }
      }
      if (localVideoTrack) {
        try {
          localVideoTrack.close();
        } catch (err) {
          console.error("[forceLeaveChannel] Error closing local video:", err);
        }
      }
      stopAllMusicTracksSync();

      // CRITICAL: Clear React state to prevent using closed tracks
      setLocalAudioTrack(null);
      setLocalVideoTrack(null);
      setMusicTrack(null);
      setRemoteUsers([]);
      setConnectionState("DISCONNECTED");

      detachClientHandlers(client);

      // Leave channel (don't await, just fire it off)
      client.leave().catch((err) => {
        console.error("[forceLeaveChannel] Error leaving channel:", err);
      });

      console.log("[forceLeaveChannel] => forceful cleanup completed");
    } catch (error) {
      console.error("[forceLeaveChannel] Fatal error:", error);
      // Ensure state is cleared even on fatal error
      setLocalAudioTrack(null);
      setLocalVideoTrack(null);
      setMusicTrack(null);
      setRemoteUsers([]);
    }
  };
  forceLeaveRef.current = forceLeaveChannel;

  // Leave on unmount so a closed overlay never keeps publishing or rejoining
  useEffect(() => () => {
    clearTimeout(reconnectTimerRef.current);
    const client = clientRef.current;
    const stillInChannel = client && !["DISCONNECTED", "DISCONNECTING"].includes(client.connectionState);
    if (stillInChannel) forceLeaveRef.current();
  }, []);

  // ==========================================================
  // 4) Remote user event handlers
  // ==========================================================
  const handleUserJoined = (user) => {
    if (!user || !user.uid) {
      console.warn("[handleUserJoined] => Invalid user data:", user);
      return;
    }
    console.log(`[handleUserJoined] => remote uid=${user.uid}`);
    setRemoteUsers((prev) => {
      const alreadyIn = prev.some((entry) => entry.uid === user.uid);
      if (alreadyIn) return prev;
      return [...prev, user];
    });
  };

  const handleUserPublished = async (user, mediaType) => {
    const client = clientRef.current;
    try {
      await client.subscribe(user, mediaType);
      if (mediaType === "video" && user.videoTrack) {
        // 2 = drop to the low stream, then audio-only, when this viewer's network degrades
        try {
          await client.setStreamFallbackOption(user.uid, 2);
        } catch (error) {
          console.warn(`[handleUserPublished] Stream fallback not available for uid=${user.uid}:`, error?.message);
        }
        const containerId = `player-${user.uid}`;
        const el = document.getElementById(containerId);
        if (el) {
          console.log(`[handleUserPublished] => playing remote video => #${containerId}`);
          user.videoTrack.play(el);
        } else {
          console.log(`[handleUserPublished] => target not found, waiting for container: #${containerId}`);
        }
      }
      if (mediaType === "audio" && user.audioTrack) {
        console.log(`[handleUserPublished] => playing remote audio => uid=${user.uid}`);
        user.audioTrack.play();
      }
      setRemoteUsers([...client.remoteUsers]);
    } catch (error) {
      console.error(`[handleUserPublished] Error subscribing to user ${user.uid}:`, error);
    }
  };

  const handleUserUnpublished = (user, mediaType) => {
    console.log(`[handleUserUnpublished] => uid=${user.uid}, type=${mediaType}`);
    const client = clientRef.current;
    setRemoteUsers([...client.remoteUsers]);
  };

  const handleUserLeft = (user) => {
    console.log(`[handleUserLeft] => uid=${user.uid}`);
    try {
      if (user.audioTrack) { user.audioTrack.stop(); }
      if (user.videoTrack) { user.videoTrack.stop(); }
    } catch (err) {
      console.warn('[handleUserLeft] Error stopping tracks:', err);
    }
    const client = clientRef.current;
    setRemoteUsers([...client.remoteUsers]);
  };

  // ==========================================================
  // 5) Local media helper methods
  // ==========================================================
  const muteAudio = async () => {
    if (!localAudioTrack) {
      console.warn("[muteAudio] => no local audio track available");
      return;
    }

    try {
      console.log("[muteAudio] => muting local mic");
      await localAudioTrack.setEnabled(false);
    } catch (err) {
      console.error("[muteAudio] Track error:", err);
      setLocalAudioTrack(null);
      throw new Error("Audio track unavailable");
    }
  };

  const unmuteAudio = async () => {
    if (!localAudioTrack) {
      console.warn("[unmuteAudio] => no local audio track available");
      return;
    }

    try {
      console.log("[unmuteAudio] => unmuting local mic");
      await localAudioTrack.setEnabled(true);
    } catch (err) {
      console.error("[unmuteAudio] Track error:", err);
      setLocalAudioTrack(null);
      throw new Error("Audio track unavailable");
    }
  };

  const enableLocalVideo = async () => {
    const client = clientRef.current;
    if (!localVideoTrack) {
      console.log("[enableLocalVideo] => creating camera track...");
      try {
        const camTrack = await AgoraRTC.createCameraVideoTrack();
        await client.publish(camTrack);
        console.log("[enableLocalVideo] => published camera track");
        setLocalVideoTrack(camTrack);
      } catch (err) {
        console.error("[enableLocalVideo] => error creating/publishing camera:", err);
        throw err;
      }
    } else {
      try {
        console.log("[enableLocalVideo] => enabling existing camera track");
        await localVideoTrack.setEnabled(true);
      } catch (err) {
        console.error("[enableLocalVideo] Track error:", err);
        setLocalVideoTrack(null);
        throw new Error("Video track unavailable");
      }
    }
  };

  const disableLocalVideo = async () => {
    if (!localVideoTrack) {
      console.warn("[disableLocalVideo] => no local video track available");
      return;
    }

    try {
      console.log("[disableLocalVideo] => disabling local camera track");
      await localVideoTrack.setEnabled(false);
    } catch (err) {
      console.error("[disableLocalVideo] Track error:", err);
      setLocalVideoTrack(null);
      throw new Error("Video track unavailable");
    }
  };

  // ==========================================================
  // 6) Music track publishing
  // ==========================================================
  const MUSIC_LOAD_TIMEOUT_MS = 30000;

  /** Stop, unpublish and close one buffer track. Safe to call twice. */
  const closeMusicTrack = async (track, { unpublish = true } = {}) => {
    if (!track) return;
    liveMusicTracksRef.current.delete(track);
    track.onEnded = null;
    try { track.stopProcessAudioBuffer?.(); } catch (_) { /* already stopped or closed */ }
    if (unpublish && clientRef.current) {
      try { await clientRef.current.unpublish(track.agoraTrack || track); } catch (_) {
        // Not published (OBS-controlled mode) or already gone.
      }
    }
    try { track.close(); } catch (_) { /* already stopped or closed */ }
    if (musicTrackRef.current === track) {
      musicTrackRef.current = null;
      setMusicTrack(null);
    }
  };

  /**
   * Load `fileBlob` (URL or blob) into a buffer-source track and start it.
   * Only one music track exists at a time: any current track is closed
   * first, and a load still in flight is invalidated. Returns undefined if
   * this load was superseded or stopped before it finished; throws on
   * timeout or Agora failure.
   */
  const publishMusicTrack = async (fileBlob, volume = 0.5, publishToChannel = true, { timeoutMs = MUSIC_LOAD_TIMEOUT_MS } = {}) => {
    const client = clientRef.current;
    if (!client) {
      console.warn("[publishMusicTrack] => No Agora client => cannot publish music");
      return undefined;
    }
    const seq = ++musicLoadSeqRef.current;
    const superseded = () => seq !== musicLoadSeqRef.current;

    if (musicTrackRef.current) await closeMusicTrack(musicTrackRef.current);

    console.log("[publishMusicTrack] => creating buffer source track => volume=", volume, "isObs=", isObs, "publishToChannel=", publishToChannel);
    let timer;
    const timeout = new Promise((_, reject) => {
      timer = setTimeout(() => {
        // Invalidate the load so the track closes itself when the download
        // finally completes instead of playing with nothing in the UI.
        if (!superseded()) musicLoadSeqRef.current += 1;
        reject(new Error("Track load timeout"));
      }, timeoutMs);
    });

    let track;
    try {
      const playLocally = publishToChannel && !isObs;
      const loadTrack = async () => {
        if (typeof fileBlob === "string" && await canStreamCrossOrigin(fileBlob)) {
          try {
            const streaming = await createStreamingMusicTrack(fileBlob, { playLocally });
            console.log("[publishMusicTrack] => streaming via <audio> element");
            return streaming;
          } catch (e) {
            console.warn("[publishMusicTrack] => streaming unavailable, downloading whole file:", e?.message);
          }
        } else if (typeof fileBlob === "string") {
          console.warn("[publishMusicTrack] => no CORS on media host, downloading whole file");
        }
        return AgoraRTC.createBufferSourceAudioTrack({
          source: fileBlob,
          encoderConfig: {
            sampleRate: 48000,
            stereo: true,
            bitrate: 320,
          },
        });
      };
      track = await Promise.race([
        loadTrack().then((t) => {
          // The download finished after a timeout or a newer load: discard.
          if (superseded()) {
            try { t.close(); } catch (_) { /* already stopped or closed */ }
            return undefined;
          }
          return t;
        }),
        timeout,
      ]);
    } finally {
      clearTimeout(timer);
    }
    if (!track) return undefined;

    liveMusicTracksRef.current.add(track);
    musicTrackRef.current = track;

    try {
      // Set volume BEFORE publish to prevent full-volume burst on new songs
      const scaledVol = Math.round(volume * MUSIC_VOLUME_SCALE);
      track.setVolume(scaledVol);
      track.startProcessAudioBuffer();

      // Play locally only when the web app owns the music playback.
      // When OBS controls music (publishToChannel=false), the OBS Player already
      // publishes to Agora and the host hears it via OBS monitoring — playing
      // locally here would bleed music into the mic.
      if (publishToChannel && !isObs) {
        // Streaming tracks monitor locally through Web Audio; buffer tracks
        // need an explicit play().
        if (typeof track.play === "function") track.play();
        console.log("[publishMusicTrack] => playing track locally");
      } else {
        console.log("[publishMusicTrack] => NOT playing locally (OBS-controlled or OBS browser source)");
      }

      if (publishToChannel) {
        await client.publish(track.agoraTrack || track);
        console.log("[publishMusicTrack] => music track published to channel");
      } else {
        console.log("[publishMusicTrack] => NOT publishing to channel (OBS Player handles Agora)");
      }

      if (superseded()) {
        await closeMusicTrack(track);
        return undefined;
      }

      // Re-apply after publish to ensure remote subscribers respect it
      track.setVolume(scaledVol);

      setMusicTrack(track);

      if (!track.isStreaming) {
        // Streaming tracks fire onEnded from the audio element themselves.
        track.on("state-changed", (curState, prevState) => {
          console.log(`[publishMusicTrack] => track state changed from ${prevState} to ${curState}`);
          if (curState === "stopped" && typeof track.onEnded === "function") {
            track.onEnded();
          }
        });
      }

      return track;
    } catch (err) {
      console.error("[publishMusicTrack] => failed to publish track:", err);
      await closeMusicTrack(track);
      throw err;
    }
  };

  /** Stop and close every music track, and invalidate any load in flight. */
  const stopMusicTrack = async () => {
    musicLoadSeqRef.current += 1;
    const tracks = Array.from(liveMusicTracksRef.current);
    if (tracks.length === 0 && !musicTrackRef.current) return;
    console.log(`[stopMusicTrack] => stopping ${tracks.length} music track(s)`);
    for (const t of tracks) await closeMusicTrack(t);
    musicTrackRef.current = null;
    setMusicTrack(null);
  };

  /** Synchronous variant for tab-close / forced teardown. */
  const stopAllMusicTracksSync = () => {
    musicLoadSeqRef.current += 1;
    for (const t of Array.from(liveMusicTracksRef.current)) {
      t.onEnded = null;
      try { t.stopProcessAudioBuffer?.(); } catch (_) { /* already stopped or closed */ }
      try { t.close(); } catch (_) { /* already stopped or closed */ }
    }
    liveMusicTracksRef.current.clear();
    musicTrackRef.current = null;
    setMusicTrack(null);
  };

  // ==========================================================
  // 7) Dynamic Role Switching Helper
  // ==========================================================
  const setClientRole = async (newRole) => {
    const client = clientRef.current;
    if (!client) return;
    try {
      console.log(`[setClientRole] => switching role to ${newRole}`);
      if (newRole === 'audience') {
        try {
          if (localVideoTrack) {
            await client.unpublish(localVideoTrack);
            localVideoTrack.close();
            setLocalVideoTrack(null);
          }
        } catch (e) {}
        try {
          if (localAudioTrack) {
            await client.unpublish(localAudioTrack);
            await localAudioTrack.setEnabled(false);
          }
        } catch (e) {}
      }
      await client.setClientRole(newRole);
    } catch (err) {
      console.error("[setClientRole] => error switching role:", err);
      throw err;
    }
  };

  // ==========================================================
  // 8) Screen Sharing Functions
  // ==========================================================
  const startScreenShare = async () => {
    const client = clientRef.current;
    if (!client) {
      console.error("[startScreenShare] => no client available");
      return;
    }
    if (isScreenSharing || localScreenTrack) {
      console.log("[startScreenShare] => already sharing; ignoring duplicate call");
      return;
    }

    try {
      console.log("[startScreenShare] => creating screen track");
      
      // Create screen sharing track
      const screenTrack = await AgoraRTC.createScreenVideoTrack({
        encoderConfig: "1080p_1",
        optimizationMode: "detail",
      });

      console.log("[startScreenShare] => screen track created, publishing...");
      
      // Unpublish camera video if sharing
      if (localVideoTrack) {
        await client.unpublish(localVideoTrack);
        console.log("[startScreenShare] => unpublished camera video");
      }

      // Publish screen track
      await client.publish(screenTrack);
      
      // Update state
      setLocalScreenTrack(screenTrack);
      setIsScreenSharing(true);
      
      console.log("[startScreenShare] => screen sharing started successfully");

      // Handle screen share ending (user clicks browser's stop sharing)
      screenTrack.on("track-ended", () => {
        console.log("[startScreenShare] => screen sharing ended by user");
        stopScreenShare();
      });

    } catch (error) {
      console.error("[startScreenShare] => error starting screen share:", error);
      setIsScreenSharing(false);
      throw error;
    }
  };

  const stopScreenShare = async () => {
    const client = clientRef.current;
    if (!client || !localScreenTrack) {
      console.log("[stopScreenShare] => no client or screen track to stop");
      return;
    }

    try {
      console.log("[stopScreenShare] => stopping screen share");
      
      // Unpublish and close screen track
      await client.unpublish(localScreenTrack);
      localScreenTrack.close();
      
      // Update state
      setLocalScreenTrack(null);
      setIsScreenSharing(false);
      
      // Re-publish camera video if available
      if (localVideoTrack) {
        await client.publish(localVideoTrack);
        console.log("[stopScreenShare] => re-published camera video");
      }
      
      console.log("[stopScreenShare] => screen sharing stopped successfully");
      
    } catch (error) {
      console.error("[stopScreenShare] => error stopping screen share:", error);
      setIsScreenSharing(false);
      throw error;
    }
  };

  // ==========================================================
  // 9) DAW Audio Track Publishing (live MediaStream)
  // ==========================================================
  const [dawAudioTrack, setDawAudioTrack] = useState(null);

  const publishDAWAudioTrack = async (mediaStream) => {
    const client = clientRef.current;
    if (!client) {
      console.warn("[publishDAWAudioTrack] => No Agora client");
      return undefined;
    }
    try {
      const audioTracks = mediaStream.getAudioTracks();
      if (!audioTracks.length) {
        console.warn("[publishDAWAudioTrack] => No audio tracks in MediaStream");
        return undefined;
      }
      console.log("[publishDAWAudioTrack] => creating custom audio track from MediaStream");
      const track = AgoraRTC.createCustomAudioTrack({
        mediaStreamTrack: audioTracks[0],
        encoderConfig: { sampleRate: 48000, stereo: true, bitrate: 192 },
      });
      await client.publish(track);
      setDawAudioTrack(track);
      console.log("[publishDAWAudioTrack] => DAW audio track published");
      return track;
    } catch (err) {
      console.error("[publishDAWAudioTrack] => failed:", err);
      return undefined;
    }
  };

  const stopDAWAudioTrack = async () => {
    const client = clientRef.current;
    if (!client || !dawAudioTrack) return;
    try {
      console.log("[stopDAWAudioTrack] => stopping & unpublishing DAW audio");
      await client.unpublish(dawAudioTrack);
      dawAudioTrack.close();
      setDawAudioTrack(null);
    } catch (err) {
      console.error("[stopDAWAudioTrack] => error:", err);
    }
  };

  latestRef.current = {
    appId, channel, token, uid, role, enableAudio, enableVideo, onJoinSuccess,
    localAudioTrack, localVideoTrack, musicTrack, dawAudioTrack,
  };

  // ==========================================================
  // Return everything your parent component needs
  // ==========================================================
  return {
    localAudioTrack,
    localVideoTrack,
    localScreenTrack,
    isScreenSharing,
    setLocalAudioTrack,
    setLocalVideoTrack,
    remoteUsers,
    connectionState,
    client: clientRef.current,
    joinChannel,
    leaveChannel,
    forceLeaveChannel,
    muteAudio,
    unmuteAudio,
    enableLocalVideo,
    disableLocalVideo,
    publishMusicTrack,
    stopMusicTrack,
    publishDAWAudioTrack,
    stopDAWAudioTrack,
    dawAudioTrack,
    setClientRole,
    startScreenShare,
    stopScreenShare,
  };
}
